import React, { useRef, useLayoutEffect } from 'react';

import { Box, xcss, media } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { expVal } from '@confluence/feature-experiments';

const endOfPageRecContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	paddingBottom: 'space.250',
	margin: 'auto',
	width: '87.5%',
	[media.above.lg]: { maxWidth: '1000px' },
});

const maxWidthStyles = xcss({
	width: 'unset',
	maxWidth: 'unset',
});

export const EndOfPageRecContainer = ({
	children,
	ssrPlaceholderIdProp,
	isDefaultWidth,
}: {
	children: React.ReactNode;
	ssrPlaceholderIdProp: any;
	isDefaultWidth: boolean;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const componentRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (!expVal('confluence_frontend_add_eop_viewport_analytics', 'isEnabled', false)) return;

		// Only fire analytics on client-side for successfully SSR'd pages (do nothing during SSR).
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR || !window?.__SSR_RENDERED__) return;

		const node = componentRef.current;
		if (node) {
			const rect = node.getBoundingClientRect();
			const isElementInViewport = Boolean(
				rect.top < window.innerHeight &&
				rect.bottom > 0 &&
				rect.left < window.innerWidth &&
				rect.right > 0,
			);

			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'rendered',
					actionSubject: 'SSR EndOfPageRecommendations',
					source: 'endOfPageRecommendations',
					attributes: {
						isElementInViewport,
					},
				},
			}).fire();
		}
	}, [createAnalyticsEvent]);

	return (
		<Box
			data-testid="test-rec"
			data-vc="end-of-page-recommendation-component"
			ref={componentRef}
			testId="test-rec"
			xcss={[endOfPageRecContainerStyles, !isDefaultWidth && maxWidthStyles]}
			{...ssrPlaceholderIdProp}
		>
			{children}
		</Box>
	);
};
